import {TreeNode} from 'primeng/api';
import {Objecttype} from '../models/Objecttype';

const NO_CATEGORY = 'Uten kategori';
const NO_GROUP = 'Uten gruppe';

// Synthetic "Uten ..." buckets always sort after real, named nodes.
function sortByLabel(a: TreeNode, b: TreeNode): number {
  const aSynthetic = a.label === NO_CATEGORY || a.label === NO_GROUP;
  const bSynthetic = b.label === NO_CATEGORY || b.label === NO_GROUP;
  if (aSynthetic !== bSynthetic) {
    return aSynthetic ? 1 : -1;
  }
  return (a.label || '').localeCompare(b.label || '');
}

function sortRecursive(nodes: TreeNode[]): void {
  nodes.sort(sortByLabel);
  for (const n of nodes) {
    if (n.children && n.children.length) {
      sortRecursive(n.children);
    }
  }
}

// Find-or-create a branch (non-selectable, expanded) node within a parent list.
function branch(children: TreeNode[], key: string, label: string): TreeNode {
  let node = children.find((c) => c.key === key);
  if (!node) {
    node = {key, label, selectable: false, expanded: true, children: []};
    children.push(node);
  }
  return node;
}

/**
 * Builds a Kategori > Gruppe > Undergruppe > Objekttype tree from the snapshots
 * stored on each objecttype. Types without a kategori/gruppe are bucketed under
 * explicit "Uten kategori" / "Uten gruppe" nodes so the tree stays uniform and
 * every type is reachable. A type that has a gruppe but no undergruppe sits as a
 * direct leaf under that gruppe.
 */
export function buildObjecttypeTree(objecttypes: Objecttype[]): TreeNode[] {
  const roots: TreeNode[] = [];

  for (const type of objecttypes) {
    const catKey = type.category ? 'cat-' + type.category.id : 'cat-none';
    const catLabel = type.category?.name || NO_CATEGORY;
    const categoryNode = branch(roots, catKey, catLabel);

    // Always one gruppe level: the real gruppe, or the synthetic "Uten gruppe".
    const grpKey = type.group ? catKey + '|grp-' + type.group.id : catKey + '|grp-none';
    const grpLabel = type.group?.name || NO_GROUP;
    let target = branch(categoryNode.children, grpKey, grpLabel).children;

    // Undergruppe is optional; only nest a level when the type has one.
    if (type.subgroup) {
      target = branch(target, grpKey + '|sub-' + type.subgroup.id, type.subgroup.name).children;
    }

    target.push({
      key: type.id,
      label: type.name,
      data: type.id,
      selectable: true,
      leaf: true,
    });
  }

  sortRecursive(roots);
  return roots;
}

// Depth-first search for the leaf node whose data matches the given objecttype id.
export function findObjecttypeNode(nodes: TreeNode[], id: string): TreeNode | null {
  for (const node of nodes) {
    if (node.data === id) {
      return node;
    }
    if (node.children && node.children.length) {
      const found = findObjecttypeNode(node.children, id);
      if (found) {
        return found;
      }
    }
  }
  return null;
}
